import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { PrimeTemplate } from 'primeng/api';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { getState } from '@ngrx/signals';
import { Topic } from '../../../models/topic';
import { Story } from '../../../models/story';
import { StoryStore } from '../../../stores/story/story.state';
import { TopicStore } from '../../../stores/topic/topic.state';

@Component({
  selector: 'app-teaser-item-dropdown',
  standalone: true,
  imports: [
    DropdownModule,
    FormFieldWrapperComponent,
    PrimeTemplate,
    FormsModule,
  ],
  templateUrl: './teaser-item-dropdown.component.html',
  styleUrl: './teaser-item-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TeaserItemDropdownComponent),
      multi: true,
    },
  ],
})
export class TeaserItemDropdownComponent {
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly topicStore = inject(TopicStore);

  @Input() label = 'Was soll verlinkt werden?';
  @Input() name = 'item';
  @Input() selection: string | null = null;
  @Input() disabled = false;
  @Input() enableEmpty = true;
  //@Output() selectionChange = new EventEmitter<{
  //  type: 'story' | 'bookmark' | 'topic' | 'link' | null;
  //  value: string | null;
  //}>();

  @Output() selectionChange = new EventEmitter<string | null>();

  touched = false;
  items: any;
  bookmarks: Bookmark[] = [];
  stories: Story[] = [];
  topics: Topic[] = [];
  options: any[] = [];

  constructor() {
    effect(() => {
      const bookmarkState = getState(this.bookmarkStore);
      const storyState = getState(this.storyStore);
      const topicState = getState(this.topicStore);
      this.setupItems();
    });
  }

  /**
   * Retrieves the appropriate icon class based on the current selection.
   *
   * Depending on the type of item corresponding to the current selection, returns
   * a specific font-awesome icon class. If the selection matches an item in the
   * bookmarkStore, storyStore, or topicStore, an associated icon class is returned.
   *
   * @return {string|undefined} The font-awesome icon class if a match is found, otherwise undefined.
   */
  get selectedIcon() {
    if (this.selection) {
      if (this.bookmarkStore.getById(this.selection))
        return 'fas fa-sticky-note';
      if (this.storyStore.getById(this.selection)) return 'fas fa-layer-group';
      if (this.topicStore.getById(this.selection)) return 'fas fa-file-lines';
      return '';
    }
    return '';
  }

  getType() {
    if (this.selection) {
      if (this.bookmarkStore.getById(this.selection)) return 'bookmark';
      if (this.storyStore.getById(this.selection)) return 'story';
      if (this.topicStore.getById(this.selection)) return 'topic';
      return null;
    }
    return null;
  }

  /**
   * Retrieves the selected label based on the current selection. It searches for the label in the bookmarkStore, storyStore, and topicStore in that order.
   *
   * @return {string|null} The selected label object if found, or null if no match exists.
   */
  get selectedLabel(): string {
    if (this.selection) {
      return (
        this.bookmarkStore.getById(this.selection)?.title ||
        this.storyStore.getById(this.selection)?.title ||
        this.topicStore.getById(this.selection)?.title ||
        ''
      );
    }
    return '';
  }

  /**
   * Initializes and sets up categorized items such as bookmarks, stories, and topics,
   * organizing them into a structured format for further use in the application.
   *
   * @return {void} This method does not return a value. It sets up the internal data structure `groupedCities`.
   */
  setupItems() {
    this.bookmarks = this.bookmarkStore.asArray();
    this.stories = this.storyStore.asArray();
    this.topics = this.topicStore.asArray();
    this.options = [
      {
        icon: 'fas fa-sticky-note',
        label: 'Ansicht',
        value: 'bookmark',
        items: this.bookmarks.map(item => {
          return {
            label: item.title,
            icon: 'fas fa-sticky-note',
            value: item._id,
          };
        }),
      },
      {
        icon: 'fas fa-file-lines',
        label: 'Bericht',
        value: 'story',
        items: this.stories.map(item => {
          return {
            label: item.title,
            icon: 'fas fa-file-lines',
            value: item._id,
          };
        }),
      },
      {
        icon: 'fas fa-book-open',
        label: 'Thema',
        value: 'topic',
        items: this.topics.map(item => {
          return {
            label: item.title,
            icon: 'fas fa-book-open',
            value: item._id,
          };
        }),
      },
    ];
  }

  /**
   * A callback function triggered when a value change occurs.
   *
   * This function takes a `value` parameter of type `Bookmark` or `null`.
   * It emits the updated value via the `selectionChange` event emitter
   * after a slight delay of 10ms.
   *
   * @param {string|null} value - The updated value to emit, can be a Bookmark object or null.
   */
  protected onChange = (value: string | null): void => {
    setTimeout(() => {
      //this.selectionChange.emit({ type: this.getType(), value });
      this.selectionChange.emit(value);
    }, 10);
  };

  /**
   * Handles the selection change event, updates the selection property,
   * and notifies the form control of the change.
   *
   * @param {any} event - The event object containing the new selection value.
   * @return {void} No return value.
   */
  onSelectionChange(event: any): void {
    this.selection = event.value;
    this.onChange(this.selection); // Notify the form control of the change
    this.onTouched();
  }

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: string | null): void {
    this.selection = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
